'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

export interface KpiCard {
  id: string;
  label: string;
  value: string;
  sub: string;
  trend: string;
  color: string;
  icon: string;
}

interface Props {
  kpiCards: KpiCard[];
  loading: boolean;
}

// Mock 6-point sparkline series per KPI id. Real headline numbers come from
// dashboard_metrics; these trend points are illustrative only and carry the
// DEMO DATA pill on each tile.
const SPARKLINE_SERIES: Record<string, number[]> = {
  revenue: [],
  margin:  [],
  cac:     [],
  rating:  [],
};

function Sparkline({ points, color }: { points: number[]; color: string }) {
  // Guard: with no trend points (empty DB) there is nothing to plot; a
  // single point or empty array would divide by zero / NaN the path.
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 28;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const xFor = (i: number) => (i / (points.length - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * h;
  const path = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="block"
    >
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Row2KPIStrip({ kpiCards, loading }: Props) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {kpiCards.map((kpi) => (
        <div
          key={kpi.id}
          className="relative rounded-xl p-5 flex flex-col gap-2"
          style={{
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #F0F0F0',
          }}
        >
          {/* DEMO DATA pill flags the sparkline, not the headline number. */}
          <DemoDataPill />
          {loading ? (
            <div className="space-y-2 animate-pulse">
              <div className="w-9 h-9 rounded-lg bg-gray-100" />
              <div className="h-7 bg-gray-100 rounded w-3/4 mt-2" />
              <div className="h-3 bg-gray-100 rounded w-full" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between pr-16">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${kpi.color}15` }}
                >
                  <Icon name={kpi.icon} size={18} style={{ color: kpi.color }} />
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: '#000150' }}>{kpi.value}</p>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{kpi.label}</p>
                <div className="flex items-center gap-1 text-[11px] font-semibold mt-1" style={{ color: '#10B981' }}>
                  {kpi.trend === 'up' && <Icon name="ArrowTrendingUpIcon" size={12} />}
                  {kpi.trend === 'down-good' && <Icon name="ArrowTrendingDownIcon" size={12} />}
                  <span>{kpi.sub}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100">
                <Sparkline points={SPARKLINE_SERIES[kpi.id] ?? [1, 1, 1, 1, 1, 1]} color={kpi.color} />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
